/**
 * trackingApi.js
 *
 * Fetches pig-tracking runs from the /assetview/tracking-runs endpoint
 * (Armadillo LiveMap database) and normalises them into the same shape used
 * by CorrLogix "line" objects, so they can be merged and rendered by the
 * All Projects / My Projects pages alongside CorrLogix and CorrMonitor cards.
 */

import { ajaxNew as ajax } from '../../../../../scanline/utils/ajax';
import {
	cacheKey,
	cachedCollection,
	cachedQuery
} from '../../../../../scanline/utils/dataCache';

const PROJECT_COLLECTION_MAX_AGE_MS = 5 * 60 * 1000;

// /tracking-runs answers with one page per call.  A Lambda response may not
// exceed API Gateway's 6 MB limit, and every run carries its whole pipeline
// path, sites, tools, RTUs and notes — so asking for all of them at once came
// back as a bare 502 Bad Gateway on any client whose cache was cold (a warm
// cache only ever requests a small delta, which is why this stayed invisible on
// machines that had loaded the page before).  We walk the pages instead.
//
// The server clamps this value and has its own default; sending it keeps the
// page size explicit at the call site.
const RUNS_PAGE_SIZE = 50;

// Safety valve on the walk: a backend that always answered hasMore:true, or a
// cursor that stopped advancing, must not spin forever.  At the default page
// size this still covers 25k runs — far beyond any real dataset.
const MAX_RUN_PAGES = 500;

// ── Retry helper (same pattern as cpSurveyApi) ────────────────────

const isNonRetryableHttpError = err =>
	err && err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429;

const withRetry = (fn, attempts = 3, baseDelayMs = 800) => {
	const run = async (remaining, delay) => {
		try {
			return await fn();
		} catch (err) {
			if (remaining <= 1 || isNonRetryableHttpError(err)) { throw err; }
			await new Promise(res => setTimeout(res, delay));
			return run(remaining - 1, delay * 2);
		}
	};
	return run(attempts, baseDelayMs);
};

const EMPTY_PAGE = {
	runs: [],
	presentIds: null,
	lastSync: null,
	delta: false,
	nextCursor: null,
	hasMore: false
};

/**
 * Parse a /tracking-runs response into the paged delta-sync envelope:
 *   { runs, presentIds, lastSync, delta, nextCursor, hasMore }
 *
 * Handles the historical plain-array shape, the Lambda-proxy { body: "<json>" }
 * shape, and the current page object.  A backend that predates paging simply
 * omits nextCursor/hasMore, which reads here as "this was the only page" — so an
 * older API still works, it just answers in one (large) response.
 */
const parseRunsResponse = res => {
	if (typeof (res && res.body) === 'string') {
		try {
			return parseRunsResponse(JSON.parse(res.body));
		} catch (err) {
			// An unparseable body is a broken response, not an empty dataset.
			// Answering EMPTY_PAGE here used to end the page walk quietly - with
			// hasMore:false and a valid-looking lastSync - so a truncated walk was
			// persisted as though it were the complete collection, which is one of
			// the ways the cached run list emptied itself.  Surface it instead: the
			// caller keeps its cached copy and reports the failure.
			throw new Error('tracking-runs: unparseable response body');
		}
	}
	if (Array.isArray(res)) {
		return { ...EMPTY_PAGE, runs: res };
	}

	const nextCursor =
		res && res.nextCursor !== undefined && res.nextCursor !== null
			? res.nextCursor
			: null;

	return {
		runs: Array.isArray(res && res.runs) ? res.runs : [],
		presentIds: Array.isArray(res && res.presentIds) ? res.presentIds : null,
		lastSync: (res && res.lastSync) || null,
		delta: !!(res && res.delta),
		nextCursor,
		// Trust hasMore only when a cursor came with it: without somewhere to
		// seek to, continuing would re-request the same page forever.
		hasMore: !!(res && res.hasMore) && nextCursor !== null
	};
};

// ── Raw API calls ─────────────────────────────────────────────────

/**
 * Fetch ONE page of tracking runs with the delta-sync envelope.  Omit clientName
 * (or pass null) for internal users who see all customers.  Pass updatedSince
 * (a previously-received lastSync watermark) to request only changed runs, and
 * cursor to continue a walk.
 *
 * Prefer fetchTrackingRunsPaged — a lone call here only sees the first page.
 */
const fetchTrackingRunsPage = ({ clientName, updatedSince, cursor, limit } = {}) => {
	const body = { limit: limit || RUNS_PAGE_SIZE };
	if (clientName) { body.clientName = clientName; }
	if (updatedSince) { body.updatedSince = updatedSince; }
	if (cursor) { body.cursor = cursor; }
	return withRetry(() =>
		ajax({
			url: '/tracking-runs',
			data: JSON.stringify(body),
			method: 'POST',
			aip: 'assetview'
		})
	).then(parseRunsResponse);
};

/**
 * Walk every page of /tracking-runs and return the combined delta-sync envelope.
 *
 * `presentIds` and `lastSync` are scope-wide and only sent with the first page,
 * so they are captured there and returned once the walk completes — applying
 * either mid-walk would reconcile against runs that have not been downloaded
 * yet, deleting them from the cache.
 *
 * `onPage` is invoked with (pageRuns, runsSoFar) after each page so a caller can
 * paint progressively instead of holding a spinner across the whole walk; it is
 * advisory and never affects the resolved value.
 *
 * The resolved envelope carries `complete`: true only when the walk reached a
 * page that said hasMore:false.  A walk cut short by MAX_RUN_PAGES resolves
 * with complete:false, so callers never treat a partial result (or its
 * scope-wide presentIds) as authoritative - see canPrunePresentIds in
 * dataCache.js.
 */
const fetchTrackingRunsPaged = async ({
	clientName,
	updatedSince,
	pageSize = RUNS_PAGE_SIZE,
	onPage
} = {}) => {
	const runs = [];
	let cursor = null;
	let meta = null;
	let pages = 0;
	let complete = false;

	for (; ;) {
		// Sequential by nature: each request needs the previous page's cursor.
		// eslint-disable-next-line no-await-in-loop
		const page = await fetchTrackingRunsPage({
			clientName,
			updatedSince,
			cursor,
			limit: pageSize
		});

		if (meta === null) {
			meta = {
				presentIds: page.presentIds,
				lastSync: page.lastSync,
				delta: page.delta
			};
		}

		runs.push(...page.runs);
		pages += 1;

		if (typeof onPage === 'function') {
			onPage(page.runs, runs);
		}

		if (!page.hasMore) {
			complete = true;
			break;
		}
		if (pages >= MAX_RUN_PAGES) {
			// Safety valve tripped: the walk is truncated, so nothing scope-wide
			// (presentIds, lastSync) may be trusted from it.
			break;
		}
		cursor = page.nextCursor;
	}

	return { runs, ...meta, complete };
};

/**
 * Fetch every tracking run (array only).  Retained for callers that just need
 * the raw runs; prefer fetchTrackingRunsCached for cache-backed delta loading.
 */
export const fetchTrackingRuns = ({ clientName } = {}) =>
	fetchTrackingRunsPaged({ clientName }).then(res => res.runs);

const uniqueCustomerNames = customers => [
	...new Set((customers || [])
		.map(customer => String(customer || '').trim())
		.filter(Boolean))
];

const dedupeRuns = runs => {
	const byId = new Map();
	runs.forEach(run => {
		if (!byId.has(run.id)) { byId.set(run.id, run); }
	});
	return [...byId.values()];
};

export const fetchTrackingRunsForCustomers = ({ customers = [], isInternal = false } = {}) => {
	const customerNames = uniqueCustomerNames(customers);

	if (customerNames.length === 1 && !isInternal) {
		return fetchTrackingRuns({ clientName: customerNames[0] });
	}

	if (customerNames.length > 1 && !isInternal) {
		return Promise.all(customerNames.map(clientName => fetchTrackingRuns({ clientName })))
			.then(results => dedupeRuns(results.reduce((all, runs) => all.concat(runs), [])));
	}

	return fetchTrackingRuns();
};

// ── Cache-backed loading (IndexedDB + delta sync) ─────────────────

/**
 * Resolve the fetch scope to a stable cache key and a fetch strategy.
 *   all    – internal users (no clientName); delta-capable
 *   single – one customer; delta-capable
 *   multi  – several customers fanned out; full-download only (each customer
 *            has its own watermark, so a single delta call cannot represent it)
 */
const resolveTrackingScope = ({ customers = [], isInternal = false } = {}) => {
	const names = uniqueCustomerNames(customers);
	if (isInternal && names.length !== 1) {
		return { mode: 'all', cacheSuffix: 'all' };
	}
	if (names.length === 1) {
		return { mode: 'single', clientName: names[0], cacheSuffix: `cust:${names[0]}` };
	}
	if (names.length > 1) {
		return { mode: 'multi', clientNames: names, cacheSuffix: `custs:${names.join('|')}` };
	}
	return { mode: 'all', cacheSuffix: 'all' };
};

/**
 * Cache-first tracking-run loader.  Returns normalised runs and:
 *   - invokes `onCached(normalisedRuns)` immediately when a cached copy exists,
 *     so the page can paint instantly, then
 *   - resolves with the freshened list once the network responds (a delta
 *     merge when the server supports it, otherwise a full replacement).
 *
 * Cached runs are stored already-normalised so the page renders them directly.
 */
export const fetchTrackingRunsCached = ({
	customers = [],
	isInternal = false,
	onCached,
	onProgress
} = {}) => {
	const scope = resolveTrackingScope({ customers, isInternal });
	const key = cacheKey('tracking', scope.cacheSuffix);

	/**
	 * Normalise each page as it lands and hand the caller the running total, so
	 * a cold-cache load paints after the first page instead of after the last.
	 * Normalising per page (rather than re-mapping the accumulator every time)
	 * keeps the walk linear in the number of runs.
	 */
	const makePageReporter = () => {
		const normalized = [];
		return {
			normalized,
			onPage: pageRuns => {
				// eslint-disable-next-line no-use-before-define
				pageRuns.forEach(run => normalized.push(normalizeTrackingRun(run)));
				if (typeof onProgress === 'function' && normalized.length > 0) {
					onProgress([...normalized]);
				}
			}
		};
	};

	const fetchFull = () => {
		if (scope.mode === 'multi') {
			// Each customer has its own watermark, so this scope stays a full
			// download; every customer is still walked page by page.
			return Promise.all(
				scope.clientNames.map(clientName => fetchTrackingRunsPaged({ clientName }))
			).then(results => {
				const runs = dedupeRuns(
					results.reduce((all, res) => all.concat(res.runs), [])
				);
				// eslint-disable-next-line no-use-before-define
				return { items: runs.map(normalizeTrackingRun), lastSync: null };
			});
		}
		const clientName = scope.mode === 'single' ? scope.clientName : undefined;
		const reporter = makePageReporter();
		return fetchTrackingRunsPaged({ clientName, onPage: reporter.onPage }).then(res => ({
			items: reporter.normalized,
			// Only persisted once every page is in: storing the watermark after a
			// partial walk would make the next delta skip the runs this one never
			// downloaded, and that delta's presentIds would then prune them.
			lastSync: res.complete ? res.lastSync : null
		}));
	};

	const fetchDelta =
		scope.mode === 'multi'
			? undefined
			: updatedSince => {
				const clientName = scope.mode === 'single' ? scope.clientName : undefined;
				return fetchTrackingRunsPaged({ clientName, updatedSince }).then(res => {
					// Older backend ignored updatedSince → force a full download.
					if (!res.delta) { return null; }
					return {
						// eslint-disable-next-line no-use-before-define
						updated: res.runs.map(normalizeTrackingRun),
						// presentIds reconciles deletions against the complete
						// scope, so it is only safe now that the walk is done.
						presentIds: Array.isArray(res.presentIds)
							? res.presentIds.map(id => `tracking:${id}`)
							: undefined,
						// Only a walk that reached the final page may be used to delete
						// cached runs; a truncated one would prune everything it never
						// downloaded (see canPrunePresentIds in dataCache.js).
						presentIdsComplete: res.complete === true,
						lastSync: res.complete ? res.lastSync : null
					};
				});
			};

	return cachedCollection({
		key,
		getId: run => run.id,
		maxAgeMs: PROJECT_COLLECTION_MAX_AGE_MS,
		fetchFull,
		fetchDelta,
		onCached
	});
};

/**
 * Pull the run object out of a run-detail response, tolerating the historical
 * Lambda-proxy { body: "<json>" } wrapping.
 */
const parseRunDetailResponse = res => {
	if (res && res.run) { return res.run; }
	if (typeof (res && res.body) === 'string') {
		try {
			const parsed = JSON.parse(res.body);
			return (parsed && parsed.run) || null;
		} catch (err) {
			return null;
		}
	}
	return null;
};

/**
 * Fetch the full detail for one run (events, RTU message counts, cell
 * towers).  Used by the Tracking run details page.
 */
export const fetchTrackingRunDetail = runId =>
	withRetry(() =>
		ajax({
			url: '/tracking-run-detail',
			data: JSON.stringify({ runId: Number(runId) }),
			method: 'POST',
			aip: 'assetview'
		})
	).then(parseRunDetailResponse);

/**
 * Public (no-login) run-detail loader for the shared link page.  Resolves the
 * run from its unguessable share token server-side and omits the auth header
 * (isAnonymous) so an external viewer can open it.  A best-effort visit context
 * is attached for the office's audit trail; the server records the request IP.
 */
export const fetchPublicTrackingRunDetail = token => {
	const clean = typeof token === 'string' ? token.trim() : '';
	if (!clean) { return Promise.resolve(null); }
	const body = {
		token: clean,
		userAgent: (window.navigator && window.navigator.userAgent) || null,
		language: (window.navigator && window.navigator.language) || null,
		referrer: document.referrer || null,
		timeZone:
			(window.Intl &&
				window.Intl.DateTimeFormat().resolvedOptions().timeZone) ||
			null,
		visitedAt: new Date().toISOString()
	};
	return withRetry(() =>
		ajax({
			url: '/tracking-share-detail',
			data: JSON.stringify(body),
			method: 'POST',
			aip: 'assetview',
			isAnonymous: true
		})
	).then(parseRunDetailResponse);
};

/**
 * Cache-backed run-detail loader: paints the last-seen detail instantly via
 * `onCached`, then revalidates from the network.  Detail payloads are cached
 * per run as opaque query values (no per-field delta merging applies).
 */
export const fetchTrackingRunDetailCached = (runId, { onCached } = {}) =>
	cachedQuery({
		key: cacheKey('tracking-detail', runId),
		fetcher: () => fetchTrackingRunDetail(runId),
		onCached
	});

// ── URL builders ──────────────────────────────────────────────────

const deriveViewport = bbox => {
	const validBbox =
		Array.isArray(bbox) &&
		bbox.length === 4 &&
		bbox.every(n => typeof n === 'number' && Number.isFinite(n)) &&
		bbox[2] >= bbox[0] &&
		bbox[3] >= bbox[1];
	if (!validBbox) { return null; }

	const lon = (bbox[0] + bbox[2]) / 2;
	const lat = (bbox[1] + bbox[3]) / 2;
	const maxSpan = Math.max(Math.abs(bbox[2] - bbox[0]), Math.abs(bbox[3] - bbox[1]));

	let zoom = 12;
	if (maxSpan <= 0.05) { zoom = 14; }
	else if (maxSpan <= 0.15) { zoom = 13; }
	else if (maxSpan <= 0.5) { zoom = 11; }
	else if (maxSpan <= 1.5) { zoom = 10; }
	else if (maxSpan <= 4) { zoom = 8; }
	else { zoom = 7; }

	return { zoom, lat, lon };
};

const normalizeBoolean = value =>
	value === true ||
	value === 1 ||
	value === '1' ||
	String(value).toLowerCase() === 'true';

const normalizeInteger = value => {
	if (value === null || value === undefined || value === '') { return null; }
	const numberValue = Number(value);
	return Number.isFinite(numberValue) ? numberValue : null;
};

/**
 * A future RunEvent is an office-scheduled event, not yet an observed run
 * event.  Do not expose it to viewers until the scheduled instant has passed.
 * Invalid or missing timestamps remain visible so a malformed record is not
 * silently hidden forever.
 */
export const isTrackingEventVisible = (event, now = Date.now()) => {
	const timestamp = event && (event.timestamp || event.TimeStamp);
	if (!timestamp) { return true; }
	const scheduledAt = new Date(timestamp).getTime();
	return Number.isNaN(scheduledAt) || scheduledAt < now;
};

/** Filter a normalized detail run at render time so an event appears promptly
 * when its scheduled instant passes, without waiting for the next API poll. */
export const filterTrackingRunEventsForDisplay = (run, now = Date.now()) => {
	if (!run) { return run; }
	const events = (run.events || []).filter(event => isTrackingEventVisible(event, now));
	const runNotes = (run.runNotes || []).filter(note => isTrackingEventVisible(note, now));
	if (events.length === (run.events || []).length && runNotes.length === (run.runNotes || []).length) {
		return run;
	}
	return {
		...run,
		events,
		runNotes
	};
};

const normalizeFiniteNumber = value => {
	if (value === null || value === undefined || value === '') { return null; }
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

const getTrackingValue = (record, ...fields) => {
	if (!record) { return null; }
	for (let index = 0; index < fields.length; index += 1) {
		const value = record[fields[index]];
		if (value !== undefined && value !== null) { return value; }
	}
	return null;
};

const normalizeTrackingSite = site => ({
	...site,
	id: getTrackingValue(site, 'id', 'ID'),
	name: getTrackingValue(site, 'name', 'Name'),
	cumulativeDistance: normalizeFiniteNumber(
		getTrackingValue(site, 'cumulativeDistance', 'CumulativeDistance')
	),
	agrTypeId: normalizeInteger(site && (
		site.agrTypeId !== undefined
			? site.agrTypeId
			: (site.AGRTypeID !== undefined ? site.AGRTypeID : site.TypeID)
	)),
	isCritical: normalizeBoolean(site && site.isCritical) ||
		normalizeBoolean(site && site.IsCritical)
});

// Detail payloads normally come from trackingService's camel-case mapper, but
// older deployed LiveMap handlers preserve SQL's Pascal-case column names.
// Normalize tool flags at the boundary so both payload shapes render alike.
const normalizeTrackingPig = pig => ({
	...pig,
	id: getTrackingValue(pig, 'id', 'ID'),
	runId: getTrackingValue(pig, 'runId', 'RunID'),
	name: getTrackingValue(pig, 'name', 'Name'),
	benchmarked: normalizeBoolean(pig && (
		pig.benchmarked !== undefined ? pig.benchmarked : pig.Benchmarked
	)),
	launchPointId: getTrackingValue(pig, 'launchPointId', 'LaunchPointID'),
	launchTime: getTrackingValue(pig, 'launchTime', 'LaunchTime'),
	trapTime: getTrackingValue(pig, 'trapTime', 'TrapTime'),
	// Legacy LiveMap detail handlers return these SQL aliases in Pascal case.
	// The detail KPIs deliberately use finite numbers, so leaving MaxDistance
	// unnormalised made every affected tool appear to have travelled zero.
	distanceTravelled: normalizeFiniteNumber(getTrackingValue(
		pig,
		'distanceTravelled',
		'DistanceTravelled',
		'MaxDistance',
		'LastDistance'
	)),
	avgVelocity: normalizeFiniteNumber(getTrackingValue(pig, 'avgVelocity', 'AvgVelocity')),
	lastVelocity: normalizeFiniteNumber(getTrackingValue(pig, 'lastVelocity', 'LastVelocity')),
	estimatedVelocity: normalizeFiniteNumber(getTrackingValue(
		pig,
		'estimatedVelocity',
		'CurrentEstimatedVelocity'
	)),
	lastEventTime: getTrackingValue(pig, 'lastEventTime', 'LastEventTime'),
	lastPassageTime: getTrackingValue(pig, 'lastPassageTime', 'LastPassageTime'),
	lastPassagePointId: getTrackingValue(pig, 'lastPassagePointId', 'LastPassagePointID'),
	lastPassageSiteName: getTrackingValue(pig, 'lastPassageSiteName', 'LastPassageSiteName')
});

const normalizeTrackingEvent = event => ({
	...event,
	id: getTrackingValue(event, 'id', 'ID'),
	runId: getTrackingValue(event, 'runId', 'RunID'),
	pigId: getTrackingValue(event, 'pigId', 'PigID'),
	eventTypeId: normalizeInteger(getTrackingValue(event, 'eventTypeId', 'RunEventTypeID')),
	timestamp: getTrackingValue(event, 'timestamp', 'TimeStamp'),
	pointId: getTrackingValue(event, 'pointId', 'PointID'),
	distance: normalizeFiniteNumber(getTrackingValue(event, 'distance', 'Distance')),
	velocity: normalizeFiniteNumber(getTrackingValue(event, 'velocity', 'Velocity')),
	cumulativeDistance: normalizeFiniteNumber(
		getTrackingValue(event, 'cumulativeDistance', 'CumulativeDistance')
	),
	siteAgrTypeId: normalizeInteger(event && (
		event.siteAgrTypeId !== undefined ? event.siteAgrTypeId : event.SiteAGRTypeID
	)),
	isCritical: normalizeBoolean(event && event.isCritical) ||
		normalizeBoolean(event && event.IsCritical),
	isInternal: normalizeBoolean(event && event.isInternal) ||
		normalizeBoolean(event && event.IsInternal)
});

/**
 * Shape a raw run-detail payload (from fetchTrackingRunDetail /
 * fetchPublicTrackingRunDetail) into the object the Tracking run details
 * page — and the run report PDF — render.
 *
 * `createdBy` / `dateCreated` are read straight off the payload: they are
 * audit fields the run list has no use for, so normalizeTrackingRun does not
 * carry them, but the details page shows them under Run information.
 *
 * `personnel` is likewise detail-only — the crew assigned to the run in
 * LiveMap (dbo.Personnel / dbo.PersonnelRole), which the Contacts popover
 * lists by role, name and phone.
 */
export const toDetailTrackingRun = raw => ({
	...normalizeTrackingRun(raw),
	token: raw.token || null,
	createdBy: raw.createdBy || null,
	dateCreated: raw.dateCreated || null,
	events: Array.isArray(raw.events) ? raw.events.map(normalizeTrackingEvent) : [],
	personnel: Array.isArray(raw.personnel) ? raw.personnel : [],
	cellTowers: Array.isArray(raw.cellTowers) ? raw.cellTowers : [],
	eventsTruncated: Boolean(raw.eventsTruncated)
});

/** Deep link to the run details page (Run details / RTUs / tool tabs). */
export const buildTrackingRunUrl = runId => `/Tracking/run/${runId}`;

/**
 * Deep link to the Tracking map focused on the run's pipeline.  Mirrors the
 * /CorrMonitor/{zoom}/{lat}/{lon} viewport-in-path convention.
 */
export const buildTrackingMapUrl = (runId, bbox) => {
	const viewport = deriveViewport(bbox);
	if (viewport) {
		const { zoom, lat, lon } = viewport;
		return `/Tracking/${zoom}/${lat.toFixed(6)}/${lon.toFixed(6)}/?run=${runId}`;
	}
	return `/Tracking?run=${runId}`;
};

// ── Data normalisation ────────────────────────────────────────────

/**
 * Convert a raw tracking run (from the trackingRuns lambda) into a shape
 * that matches the CorrLogix "line" objects, so ProjectCard / SummaryModal
 * and the shared filter utilities can render them.
 *
 * Tracking-specific extras (not present on other sources):
 *   pipeline, pigs, sites, rtus, path, offPipelinePoints, runNotes,
 *   toolCount, siteCount, criticalSiteCount, rtuCount, metric,
 *   trackingUrl (details page) and trackingMapUrl (map focused on the run).
 */
export function normalizeTrackingRun(run) {
	const rawStart = run.runStart || run.dateCreated;
	const startDate = rawStart ? new Date(rawStart) : undefined;
	const endDate = run.runEnd ? new Date(run.runEnd) : undefined;

	const pigs = Array.isArray(run.pigs) ? run.pigs.map(normalizeTrackingPig) : [];
	const toolCount = run.toolCount || pigs.length || 0;
	const siteCount = run.siteCount || 0;
	const rtuCount = run.rtuCount || 0;
	const runNotes = Array.isArray(run.notes)
		? run.notes.filter(note => !(note && note.isInternal === true))
		: [];

	// ── Tool-run progress within this run ─────────────────────
	// active    = launched and not yet trapped (currently running)
	// completed = trapped (finished)
	// remaining = not yet launched
	const activeToolCount = pigs.filter(pig => pig.launchTime && !pig.trapTime).length;
	const completedToolCount = pigs.filter(pig => Boolean(pig.trapTime)).length;
	const remainingToolCount = Math.max(toolCount - activeToolCount - completedToolCount, 0);

	// ── Tracking method — Conventional / Remote / Hybrid ──────
	// Prefer an explicit backend field when present; otherwise derive from
	// RTU coverage: no RTUs → Conventional (manual crews), RTUs at all/most
	// sites → Remote, partial RTU coverage → Hybrid.
	const trackingMethod = run.trackingMethod
		|| (rtuCount === 0
			? 'Conventional'
			: (siteCount === 0 || rtuCount >= siteCount ? 'Remote' : 'Hybrid'));

	return {
		// ── Identity ────────────────────────────────────────────
		id: `tracking:${run.id}`,
		runId: run.id,
		name: run.name || `Run ${run.id}`,
		customer: (run.pipeline && run.pipeline.company) || '',
		source: 'tracking',
		token: run.token || null,

		// ── Metadata ────────────────────────────────────────────
		facilityName: null,
		operatingArea: null,
		lineLicenseNumber: null,

		// ── Primary survey (mirrors CorrLogix primarySurvey shape) ──
		primarySurvey: {
			type: 'TRACKING',
			status: run.status || 'ACTIVE',
			start: startDate,
			end: endDate,
			jobId: run.jobNumber,
			strJobId: run.jobNumber,
			surveyGuid: null
		},

		// ── Tracking data ───────────────────────────────────────
		pipeline: run.pipeline || {},
		pigs,
		sites: Array.isArray(run.sites) ? run.sites.map(normalizeTrackingSite) : [],
		rtus: Array.isArray(run.rtus) ? run.rtus : [],
		offPipelinePoints: Array.isArray(run.offPipelinePoints) ? run.offPipelinePoints : [],
		runNotes,
		path: Array.isArray(run.path) ? run.path : [],
		toolCount,
		siteCount,
		criticalSiteCount: run.criticalSiteCount || 0,
		rtuCount,
		activeToolCount,
		remainingToolCount,
		completedToolCount,
		trackingMethod,
		metric: run.metric !== false,
		comments: run.comments || null,
		maxVelocity: run.maxVelocity,
		timeZone: run.timeZone || null,

		// ── Commercial fields — no backend source yet, kept blank so the
		// card/summary render the placeholder until the API provides them ──
		poNumber: run.poNumber || null,
		projectManager: run.projectManager || null,

		// ── Geographic location — same conventions as CorrMonitor ──
		location: Array.isArray(run.location) && run.location.length === 2
			? run.location
			: null,
		bbox: Array.isArray(run.bbox) && run.bbox.length === 4
			? run.bbox
			: null,

		// ── Fields expected by shared card/filter code ──────────
		surveyCount: undefined,
		recentSurveyCount: undefined,
		jobCount: undefined,
		surveyTypesList: '',
		tags: null,
		jobs: [],
		scanlineEnabled: false,

		// ── Tracking deep-links ─────────────────────────────────
		trackingUrl: buildTrackingRunUrl(run.id),
		trackingMapUrl: buildTrackingMapUrl(run.id, run.bbox)
	};
}
